import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Form,
  Header,
  Label,
  Message,
  Modal,
  Segment,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import { QRCodeSVG } from 'qrcode.react';
import LoginHistoryTable from './LoginHistoryTable';
import { createPasskeyV815 } from '../helpers/webauthnV815';

const formatSecurityTimeV815 = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const SecuritySetting = () => {
  const [overview, setOverview] = useState({
    totp_enabled: false,
    passkeys: [],
  });
  const [sessions, setSessions] = useState([]);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [deletingPasskey, setDeletingPasskey] = useState(null);
  const [setupData, setSetupData] = useState({ secret: '', uri: '' });
  const [code, setCode] = useState('');
  const [passkeyName, setPasskeyName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const loadSecurity = async () => {
    const [securityRes, sessionsRes] = await Promise.all([
      API.get('/api/security'),
      API.get('/api/security/sessions'),
    ]);
    if (securityRes.data?.success) {
      setOverview(securityRes.data.data);
    } else {
      showError(securityRes.data?.message);
    }
    if (sessionsRes.data?.success) {
      setSessions(sessionsRes.data.data || []);
    } else {
      showError(sessionsRes.data?.message);
    }
  };

  useEffect(() => {
    loadSecurity().catch((error) => showError(error.message));
  }, []);

  const handleEnableClick = async () => {
    const res = await API.get('/api/user/2fa/setup');
    if (res.data?.success) {
      setSetupData(res.data.data);
      setShowEnableModal(true);
    } else {
      showError(res.data?.message);
    }
  };

  const handleEnableSubmit = async () => {
    const res = await API.post('/api/user/2fa/enable', {
      secret: setupData.secret,
      code,
    });
    if (res.data?.success) {
      showSuccess(res.data.message);
      setShowEnableModal(false);
      setCode('');
      await loadSecurity();
    } else {
      showError(res.data?.message);
    }
  };

  const handleDisableSubmit = async () => {
    const res = await API.post('/api/user/2fa/disable', { code });
    if (res.data?.success) {
      showSuccess(res.data.message);
      setShowDisableModal(false);
      setCode('');
      await loadSecurity();
    } else {
      showError(res.data?.message);
    }
  };

  const addPasskey = async () => {
    setLoading(true);
    try {
      const begin = await API.post('/api/security/passkeys/register/begin', {
        password,
      });
      if (!begin.data?.success) throw new Error(begin.data?.message);
      const ceremonyId = begin.data.data.ceremony_id;
      const credential = await createPasskeyV815(begin.data.data.options);
      const finish = await API.post(
        '/api/security/passkeys/register/finish?ceremony_id=' +
          encodeURIComponent(ceremonyId) +
          '&name=' +
          encodeURIComponent(passkeyName || 'Passkey'),
        credential
      );
      if (!finish.data?.success) throw new Error(finish.data?.message);
      showSuccess('Passkey 已添加');
      setShowPasskeyModal(false);
      setPassword('');
      setPasskeyName('');
      await loadSecurity();
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const removePasskey = async () => {
    if (!deletingPasskey) return;
    setLoading(true);
    try {
      const res = await API.delete(
        '/api/security/passkeys/' + deletingPasskey.id,
        { data: { password } }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      showSuccess('Passkey 已移除');
      setDeletingPasskey(null);
      setPassword('');
      await loadSecurity();
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (session) => {
    const res = await API.delete(
      '/api/security/sessions/' + encodeURIComponent(session.id)
    );
    if (!res.data?.success) {
      showError(res.data?.message);
      return;
    }
    showSuccess('设备已登出');
    if (res.data.data?.current_revoked) {
      localStorage.removeItem('user');
      window.location.assign('/login');
      return;
    }
    await loadSecurity();
  };

  return (
    <div>
      <Header as='h3'>两步验证</Header>
      <Segment>
        <Header as='h4'>动态验证码</Header>
        <p>动态验证码与 Passkey 可以同时启用，登录或验证时任选一种即可。</p>
        {overview.totp_enabled ? (
          <>
            <Label color='green'>已启用</Label>{' '}
            <Button basic color='red' onClick={() => setShowDisableModal(true)}>
              停用
            </Button>
          </>
        ) : (
          <>
            <Label color='grey'>未启用</Label>{' '}
            <Button primary onClick={handleEnableClick}>
              启用动态验证码
            </Button>
          </>
        )}

        <Divider />

        <Header as='h4'>Passkey 与生物认证</Header>
        <p>
          可使用 Windows Hello、手机指纹、Face ID 或设备
          PIN。服务器只保存公开凭据，不会取得生物资料。
        </p>
        <Button
          icon='plus'
          content='添加 Passkey'
          onClick={() => setShowPasskeyModal(true)}
        />
        {overview.passkeys?.length ? (
          <Table basic='very' compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>名称</Table.HeaderCell>
                <Table.HeaderCell>添加时间</Table.HeaderCell>
                <Table.HeaderCell>最后使用</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {overview.passkeys.map((passkey) => (
                <Table.Row key={passkey.id}>
                  <Table.Cell>{passkey.name}</Table.Cell>
                  <Table.Cell>
                    {formatSecurityTimeV815(passkey.created_at)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatSecurityTimeV815(passkey.last_used_at)}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      basic
                      negative
                      size='tiny'
                      icon='trash'
                      content='移除'
                      onClick={() => setDeletingPasskey(passkey)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <Message size='small'>尚未添加 Passkey。</Message>
        )}
      </Segment>

      <Divider />

      <Header as='h3'>已登录设备</Header>
      <Segment>
        <Table celled compact stackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>设备型号</Table.HeaderCell>
              <Table.HeaderCell>IP 地址</Table.HeaderCell>
              <Table.HeaderCell>登录时间</Table.HeaderCell>
              <Table.HeaderCell>最后在线</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sessions.map((session) => (
              <Table.Row key={session.id} positive={session.current}>
                <Table.Cell>
                  {session.device_name}{' '}
                  {session.current && <Label size='tiny'>本设备</Label>}
                </Table.Cell>
                <Table.Cell>{session.last_ip || session.first_ip}</Table.Cell>
                <Table.Cell>
                  {formatSecurityTimeV815(session.created_at)}
                </Table.Cell>
                <Table.Cell>
                  {formatSecurityTimeV815(session.last_seen_at)}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    basic
                    negative
                    size='tiny'
                    icon='sign-out'
                    content={session.current ? '登出本设备' : '登出设备'}
                    onClick={() => revokeSession(session)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>

      <Divider />

      <Header as='h3'>登录尝试历史</Header>
      <Segment>
        <LoginHistoryTable />
      </Segment>

      <Modal
        open={showEnableModal}
        onClose={() => setShowEnableModal(false)}
        size='tiny'
      >
        <Modal.Header>设置动态验证码</Modal.Header>
        <Modal.Content>
          <p>使用身份验证器扫描二维码：</p>
          <div style={{ textAlign: 'center', margin: '20px' }}>
            {setupData.uri && <QRCodeSVG value={setupData.uri} size={200} />}
          </div>
          <p>
            手动密钥：<strong>{setupData.secret}</strong>
          </p>
          <Form.Input
            label='动态验证码'
            value={code}
            onChange={(_, { value }) => setCode(value)}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowEnableModal(false)}>取消</Button>
          <Button primary onClick={handleEnableSubmit}>
            确认启用
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        size='tiny'
      >
        <Modal.Header>停用动态验证码</Modal.Header>
        <Modal.Content>
          <Form.Input
            label='动态验证码'
            value={code}
            onChange={(_, { value }) => setCode(value)}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowDisableModal(false)}>取消</Button>
          <Button negative onClick={handleDisableSubmit}>
            确认停用
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={showPasskeyModal}
        onClose={() => setShowPasskeyModal(false)}
        size='tiny'
      >
        <Modal.Header>添加 Passkey</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label='名称'
              placeholder='例如：办公室 Windows Hello'
              value={passkeyName}
              onChange={(_, { value }) => setPasskeyName(value)}
            />
            <Form.Input
              label='当前密码'
              type='password'
              value={password}
              onChange={(_, { value }) => setPassword(value)}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowPasskeyModal(false)}>取消</Button>
          <Button
            primary
            loading={loading}
            disabled={!password}
            onClick={addPasskey}
          >
            继续
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={Boolean(deletingPasskey)}
        onClose={() => setDeletingPasskey(null)}
        size='tiny'
      >
        <Modal.Header>移除 Passkey</Modal.Header>
        <Modal.Content>
          <p>输入当前密码以移除 {deletingPasskey?.name}。</p>
          <Form.Input
            label='当前密码'
            type='password'
            value={password}
            onChange={(_, { value }) => setPassword(value)}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDeletingPasskey(null)}>取消</Button>
          <Button
            negative
            loading={loading}
            disabled={!password}
            onClick={removePasskey}
          >
            移除
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default SecuritySetting;
